/**
 * @file useOpenFileInTab.ts
 * @description «Открыть файл» из API кабинета в новой вкладке (phase19.16,
 * партия «Юридические документы»: файлы сертификатов производителя; образец —
 * `useOpenLegalFile` документов).
 *
 * Файл отдаёт API под cookie и заголовком кабинета вкладки — прямой ссылкой
 * его не открыть: скачиваем Blob и показываем по `blob:`-адресу. Вкладка
 * открывается синхронно по нажатию (иначе браузер заблокирует окно),
 * `opener` обнуляется (вкладка не управляет кабинетом), адрес ставится после
 * ответа. Вкладку не дали открыть — файл скачивается.
 */

import { useCallback } from "react";

import { useNotify } from "../components/ui/NotifyProvider";
import { ApiError, type ApiFileResponse } from "../lib/api-client";

/** Через сколько освободить `blob:`-адрес (вкладка успевает его прочитать). */
const REVOKE_AFTER_MS = 60_000;

/** Открыть файл: `download` — запрос файла; `errorText` — текст, если сервер не дал своего. */
export function useOpenFileInTab(): (download: () => Promise<ApiFileResponse>, errorText?: string) => void {
  const notify = useNotify();
  return useCallback(
    (download, errorText = "Не удалось открыть файл.") => {
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      void download()
        .then(({ blob, filename }) => {
          const url = URL.createObjectURL(blob);
          if (tab) {
            tab.location.href = url;
          } else {
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            link.click();
          }
          window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
        })
        .catch((err: unknown) => {
          tab?.close();
          notify.error(err instanceof ApiError ? err.message : errorText);
        });
    },
    [notify],
  );
}
